import { forwardRef, memo, useCallback, useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import findIndex from 'lodash/findIndex';
import thru from 'lodash/thru';
import map from 'lodash/map';
import isString from 'lodash/isString';
import isPlainObject from 'lodash/isPlainObject';
import clsx from 'clsx';
import { useLocation, useSearchParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
// Material UI imports
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import CardHeader, { type CardHeaderProps } from '@mui/material/CardHeader';
import { tabClasses } from '@mui/material/Tab';
import Tabs, { tabsClasses } from '@mui/material/Tabs';
// local imports
import { TabbarItem } from '../models/tabbarItem';
import TabLink from '../mixins/TabLink';
import TabParamLink from './TabParamLink';
// SCSS imports
import { header, withoutAction, withAction, withWideAction } from './CardTabbar.module.scss';

type CardTabbarProps = {
  readonly items: TabbarItem[];
  readonly action?: ReactNode | null;
  readonly wideAction?: boolean | null;
  readonly withoutScroll?: boolean | null;
  readonly onChange?: (index: number) => void;
  readonly savedPathname?: string;
  /**
   * When provided, the active tab is driven by this URL query parameter name instead of the
   * pathname. Each `TabbarItem.link` must be the plain string value for that parameter
   * (e.g. `"published"`, `"draft"`, `"history"`). Clicking a tab updates the query param via
   * `setSearchParams` — other existing search params are preserved — rather than navigating
   * to a new route. Falls back to the first tab when the param is absent.
   */
  readonly tabParam?: string | null;
  readonly sx?: CardHeaderProps['sx'];
  // React built-in
  readonly children?: ReactNode[] | null;
}

const CardTabbar = forwardRef<HTMLElement, CardTabbarProps>(({
  items,
  action,
  wideAction = false,
  withoutScroll = false,
  onChange,
  savedPathname,
  tabParam,
  sx,
  children
}, ref) => {
  const theme = useTheme();
  const tabsRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const currentPathname = savedPathname || pathname;
  const paramValue = tabParam ? searchParams.get(tabParam) : null;

  const activeIndex = thru(
    tabParam
      ? findIndex(items, ({ link }) => isString(link) && link === paramValue)
      : findIndex(items, ({ active, link }) => Boolean(link) && (
          active || link === currentPathname || (link as Record<string, string>)[currentPathname] === currentPathname
        )),
    (idx) => idx < 0 ? 0 : idx
  );
  const tabAction = items?.[activeIndex]?.action;

  const renderItem = useCallback(({ title, values, link, disabled }: TabbarItem, index: number) => {
    const titleStr = isString(title)
      ? <FormattedMessage id={title} values={values} defaultMessage={title}/>
      : title;
    const tabActionOnly = tabAction && action && activeIndex === index ? (
      <Box pl={1} display="flex" alignItems="center" zIndex={100}>
        {tabAction}
      </Box>
    ) : undefined;
    const label = tabActionOnly ? (
      <Box display="flex" alignItems="center">
        {titleStr}
        {tabActionOnly}
      </Box>
    ) : titleStr;

    if (tabParam) {
      return (
        <TabParamLink
            key={index}
            value={index}
            label={label}
            disabled={disabled || !link}
            tabParam={tabParam}
            paramValue={isString(link) ? link : ''}
        />
      );
    }

    const path = (link && (isString(link)
      ? link
      : isPlainObject(link) && link[currentPathname]
    )) || undefined;
    return (
      <TabLink
          key={index}
          value={index}
          label={label}
          to={path || ''}
          disabled={disabled || !path}
      />
    );
  }, [activeIndex, action, tabAction, tabParam, currentPathname]);

  useLayoutEffect(() => onChange?.(activeIndex), [activeIndex, onChange]);

  // MUI scrolls the selected tab into view when the value or the tab widths change, but not when the
  // scroll viewport itself shrinks — a narrowing card then leaves the selected tab outside it, under
  // a label that contradicts the content below. MUI also aligns the end of a tab wider than the
  // viewport, hiding the start of its label; align the start of such a tab instead.
  useEffect(() => {
    const scroller = tabsRef.current?.querySelector<HTMLElement>(`.${tabsClasses.scroller}`);
    if (!scroller || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      const selected = scroller.querySelector<HTMLElement>(`.${tabClasses.selected}`);
      if (!selected) return;
      const tab = selected.getBoundingClientRect();
      const view = scroller.getBoundingClientRect();
      const outsideStart = tab.left - view.left;
      scroller.scrollLeft += outsideStart < 0 || tab.width > view.width
        ? outsideStart
        : Math.max(tab.right - view.right, 0);
    });
    observer.observe(scroller);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <CardHeader
          ref={ref}
          sx={sx}
          disableTypography
          className={clsx(header, {
            [withoutAction]: !action && !tabAction,
            [withAction]: (action || tabAction) && !wideAction,
            [withWideAction]: (action || tabAction) && wideAction
          })}
          title={
            <Tabs
                ref={tabsRef}
                value={activeIndex}
                indicatorColor="primary"
                textColor="secondary"
                allowScrollButtonsMobile={isMobile}
                centered={withoutScroll && !isMobile ? true : undefined}
                variant={withoutScroll && !isMobile ? 'fullWidth' : 'scrollable'}
            >
              {map(items, renderItem)}
            </Tabs>
          }
          action={action || tabAction}
      />
      {children?.[activeIndex]}
    </>
  );
});

CardTabbar.displayName = 'CardTabbar';

export default memo(CardTabbar);
